import React from 'react';
import { Container, Row, Col, Carousel, Button } from 'react-bootstrap';
import { ChevronRight } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import FeedSection from '../feed/FeedSection';
import MaterialsFeedSection from '../feed/MaterialsFeedSection';
import { isCrawlFeed } from '../../utils/crawlFeedAdapter';

// Crawl-backed feeds (crawl=recent) render without a per-feed title, even when
// showFeedTitle is on. On StatePage the Updates branch holds a single feed whose
// name is the branch label, so the FeedSection title repeats the carousel
// heading directly above it. Checked per feed rather than per carousel so a
// mixed carousel keeps titles on everything else.
//
// isCrawlFeed expects a bare param string; apiEndpoint may carry a leading & or
// ? depending on which config produced it.
const isCrawlBackedFeed = (apiEndpoint) => isCrawlFeed((apiEndpoint || '').replace(/^[&?]+/, ''));

/**
 * DeskCarousel component - Displays feeds in a simple rotating carousel
 * 
 * For the homepage, ALL section types (normal feeds and carousel departments) 
 * are flattened into a single carousel that rotates through one feed at a time.
 * This ensures consistent sizing within the Col md={4} grid layout.
 * 
 * @param {Object} props
 * @param {string} props.title - Section title
 * @param {React.Element} props.icon - Icon component
 * @param {Array} props.sections - Array of section configurations from feedsConfig
 * @param {string} props.viewAllLink - Link to view all content
 * @param {number} props.intervalDuration - Time between carousel slides in ms
 * @param {number} props.itemsPerSection - Items per feed section
 * @param {boolean} props.showAll - If true, ignores showOnHomePage filtering (for SectionPages)
 * @param {boolean} props.showFeedTitle - If true, shows title inside each FeedSection (default: true)
 * @param {boolean} props.useViewAllLinkForTitle - If true, clicking title uses viewAllLink directly instead of generating feed URL (for AgencyPage navigation)
 * @param {boolean} props.useMaterialsFeed - If true, uses MaterialsFeedSection instead of FeedSection (for committee materials)
 * @param {boolean} props.disableNavigation - If true, disables title click navigation and hides chevron (for StatePage)
 * @param {boolean} props.hideTitle - If true, hides the entire header section (for HomePage state feeds)
 */
const DeskCarousel = ({ 
  title, 
  icon, 
  sections = [], 
  viewAllLink, 
  intervalDuration = 30000,
  itemsPerSection = 1,
  showAll = false,
  showFeedTitle = true,
  useViewAllLinkForTitle = false,
  useMaterialsFeed = false,
  disableNavigation = false,
  hideTitle = false
}) => {
  const navigate = useNavigate();
  
  // Flatten ALL feeds into a single array for carousel rotation
  const allFeeds = flattenSectionsToFeeds(sections, showAll);

  // Carousel departments (White House, House, Senate, cabinet departments) own a
  // page of their own. flattenSectionsToFeeds carries that link through as
  // `departmentLink`, so the heading can prefer it over the generated
  // /feed/{schema}/{table} URL — that's how these headings behaved when topics
  // still rendered through SectionPage/MultiDepartmentSection.
  const departmentLink = allFeeds.length > 0 ? allFeeds[0].departmentLink : null;

  /**
   * Generate feed URL from apiEndpoint for FullFeedView navigation
   * Used when showFeedTitle={false} so the carousel title acts as the feed link
   *
   * A missing table returns null rather than a 'default' placeholder: an
   * aggregate endpoint (a schema-level recents feed, a crawl feed) has no single
   * table behind it, and /feed/{schema}/default is a route with nothing on it.
   * Returning null lets handleTitleClick fall through to viewAllLink instead.
   */
  const generateFeedUrl = (apiEndpoint) => {
    if (!apiEndpoint) return null;
    
    try {
      let cleanEndpoint = apiEndpoint;
      if (cleanEndpoint.startsWith('&') || cleanEndpoint.startsWith('?')) {
        cleanEndpoint = cleanEndpoint.substring(1);
      }
      
      const urlParams = new URLSearchParams(cleanEndpoint);
      const schema = urlParams.get('schema') || 'united_states_of_america';
      const table = urlParams.get('table') || urlParams.get('table_name');

      if (!table) return null;
      
      return `/feed/${schema}/${table}`;
    } catch (error) {
      console.error('Error generating feed URL:', error);
      return null;
    }
  };

  /**
   * Navigate to a URL using React Router (SPA navigation)
   * Falls back to window.open for external URLs
   */
  const handleNavigation = (url) => {
    if (!url) return;
    
    // Check if it's an external URL
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.open(url, "_blank");
      return;
    }
    
    // Use React Router for internal navigation (smooth SPA transition)
    window.scrollTo(0, 0);
    navigate(url);
  };

  /**
   * Handle title click navigation
   * - When disableNavigation={true}: Do nothing (for StatePage branch titles)
   * - When useViewAllLinkForTitle={true}: Use viewAllLink directly (for AgencyPage, custom routes)
   * - When useMaterialsFeed={true}: Use viewAllLink directly (materials have different URL structure)
   * - When this carousel is one department (White House, House, Senate, a cabinet
   *   department): use its department page
   * - When showFeedTitle={true} (HomePage): Use viewAllLink (goes to section page)
   * - When showFeedTitle={false} (SectionPage): Generate feed URL (goes to FullFeedView)
   */
  const handleTitleClick = () => {
    // If navigation is disabled, do nothing
    if (disableNavigation) return;

    // If useViewAllLinkForTitle is true OR useMaterialsFeed is true, use viewAllLink directly
    // Materials feeds have a different URL structure that generateFeedUrl can't handle
    if ((useViewAllLinkForTitle || useMaterialsFeed) && viewAllLink) {
      handleNavigation(viewAllLink);
      return;
    }

    // A department carousel goes to the department page, not to the first feed's
    // full-feed view. Checked before the generateFeedUrl branch below, which
    // would otherwise swallow it whenever the feed titles are hidden.
    if (!showFeedTitle && departmentLink) {
      handleNavigation(departmentLink);
      return;
    }
    
    // ORIGINAL LOGIC: If FeedSection titles are hidden, this carousel title should go to FullFeedView
    if (!showFeedTitle && allFeeds.length > 0 && allFeeds[0].apiEndpoint) {
      const feedUrl = generateFeedUrl(allFeeds[0].apiEndpoint);
      if (feedUrl) {
        handleNavigation(feedUrl);
        return;
      }
    }
    
    // Default behavior: use viewAllLink (for HomePage where FeedSection shows its own title)
    if (viewAllLink) {
      handleNavigation(viewAllLink);
    }
  };

  // If no feeds, show empty state (but skip if hideTitle is true)
  if (allFeeds.length === 0) {
    if (hideTitle) {
      return null; // Don't show anything if no feeds and title is hidden
    }
    return (
      <div className="section-carousel mb-3">
        <div id="homePageBranchRow">
          <h4 style={{ cursor: disableNavigation ? 'default' : 'pointer' }}>
            {icon}
            {title}
          </h4>
        </div>
        <div id={`${title.toLowerCase().replace(/[^a-z0-9]/g, '')}Con`}>
          <div style={{ textAlign: 'center', color: '#6c757d', padding: '2rem' }}>
            No content available for {title}
          </div>
        </div>
      </div>
    );
  }

  // Choose the appropriate feed component
  const FeedComponent = useMaterialsFeed ? MaterialsFeedSection : FeedSection;

  // Recents cards carry a source header above the title that ordinary cards
  // don't. The slide height in HomePage.css is sized for title + meta + footer
  // + View More, so that extra row pushes the meta line past the bottom edge
  // and the card's overflow:hidden clips it. This class lets the stylesheet
  // give those carousels the row back.
  const hasRecentsFeed = allFeeds.some((feed) => feed.isRecentsItem);

  // Generate container ID safely (handle empty title)
  const containerId = title ? `${title.toLowerCase().replace(/[^a-z0-9]/g, '')}Con` : 'carouselCon';

  return (
    <div className={`section-carousel ${hasRecentsFeed ? 'section-carousel--recents' : ''} mb-3`}>
      {/* Only render header if hideTitle is false */}
      {!hideTitle && (
        <div id="homePageBranchRow">
          <h4 
            id="branchHeading" 
            onClick={handleTitleClick} 
            style={{ cursor: disableNavigation ? 'default' : 'pointer' }}
          >
            {icon}
            <b id="branchHeadingBold">{title}</b>
            {!disableNavigation && (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-right" viewBox="0 0 16 16" id="clickChevron">
                <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
              </svg>
            )}
          </h4>
        </div>
      )}

      <div id={containerId}>
        <Carousel controls={false} interval={intervalDuration}>
          {allFeeds.map((feed, index) => {
            // A crawl feed's own title repeats the carousel heading above it,
            // so it renders untitled. View More stays on: FeedSection resolves
            // it to viewAllLink for crawl feeds rather than to a /feed route.
            const isCrawl = isCrawlBackedFeed(feed.apiEndpoint);

            return (
              <Carousel.Item key={feed.id || index} interval={intervalDuration}>
                <Col id="homeCol">
                  <div id="columnLefttPadding">
                    <FeedComponent
                      title={showFeedTitle && !isCrawl ? feed.title : ''}
                      apiEndpoint={feed.apiEndpoint}
                      feedName={feed.feedName}
                      branch={feed.branch}
                      topic={feed.topic}
                      buttonName={feed.buttonName}
                      viewAllLink={feed.viewAllLink}
                      maxItems={itemsPerSection}
                      showViewMore={true}
                      isRecentsItem={feed.isRecentsItem}
                      recentsSchema={feed.recentsSchema}
                    />
                  </div>
                </Col>
              </Carousel.Item>
            );
          })}

          {/* View All slide at the end - only show if navigation is enabled and viewAllLink exists */}
          {!disableNavigation && viewAllLink && (
            <Carousel.Item interval={intervalDuration}>
              <Col id="homeCol">
                <div id="columnLefttPadding">
                  <div id="viewFullPageButton">
                    <Button 
                      id="viewFullLegislativePageButton" 
                      size="lg"
                      onClick={() => viewAllLink && handleNavigation(viewAllLink)}
                    >
                      {useViewAllLinkForTitle 
                        ? `View ${title} page`
                        : `View all ${title}`
                      }
                    </Button>
                  </div>
                </div>
              </Col>
            </Carousel.Item>
          )}
        </Carousel>
      </div>
    </div>
  );
};

/**
 * Flattens all section types into a simple array of feeds
 * 
 * - Normal feeds (type: 'normal') are passed through directly
 * - Carousel departments (type: 'carousel') have their carouselItems extracted,
 *   and each extracted feed keeps a `departmentLink` back to the department page
 * - Only includes items where showOnHomePage !== false (unless showAll is true)
 *
 * Each feed is rebuilt field by field rather than spread, so anything a caller
 * puts on a section is dropped unless it is listed here. isRecentsItem and
 * recentsSchema are carried through for that reason: they are what FeedSection
 * needs to render the per-item source header and point it at
 * /feed/{recentsSchema}/{item table} instead of at the aggregation table.
 * 
 * @param {Array} sections - Mixed array of normal and carousel sections
 * @param {boolean} showAll - If true, ignores showOnHomePage filtering
 * @returns {Array} - Flat array of feed configurations
 */
function flattenSectionsToFeeds(sections, showAll = false) {
  const feeds = [];

  sections.forEach(section => {
    // Skip sections where showOnHomePage is explicitly false (unless showAll is true)
    if (!showAll && section.showOnHomePage === false) return;

    if (section.type === 'normal') {
      // Normal feed - add directly if showOnHomePage is not false (or showAll is true)
      if (showAll || section.showOnHomePage !== false) {
        feeds.push({
          id: section.id || `feed-${feeds.length}`,
          title: section.title,
          apiEndpoint: section.apiEndpoint,
          feedName: section.feedName,
          branch: section.branch,
          topic: section.topic,
          buttonName: section.buttonName,
          viewAllLink: section.viewAllLink,
          departmentLink: null,
          isRecentsItem: section.isRecentsItem === true,
          recentsSchema: section.recentsSchema || null
        });
      }
    } else if (section.type === 'carousel' && section.carouselItems) {
      // Carousel department - extract each feed item
      const deptTitle = section.departmentTitle || '';
      
      section.carouselItems.forEach(item => {
        // Skip 'viewall' type items and 'member' type items - we add our own view all at the end
        if (item.type === 'viewall' || item.type === 'member') return;
        
        // Skip items without apiEndpoint
        if (!item.apiEndpoint) return;

        // Skip items where showOnHomePage is explicitly false (unless showAll is true)
        if (!showAll && item.showOnHomePage === false) return;
        
        feeds.push({
          id: item.id || `${section.id}-${feeds.length}`,
          // Combine department name with feed title for clarity
          title: deptTitle ? `${deptTitle} - ${item.title}` : item.title,
          apiEndpoint: item.apiEndpoint,
          feedName: item.feedName,
          branch: item.branch,
          topic: item.topic,
          buttonName: item.buttonName,
          viewAllLink: section.departmentLink || section.viewAllLink,
          departmentLink: section.departmentLink || null,
          isRecentsItem: (item.isRecentsItem ?? section.isRecentsItem) === true,
          recentsSchema: item.recentsSchema || section.recentsSchema || null
        });
      });
    }
  });

  return feeds;
}

export default DeskCarousel;